import {
  Button,
  DateInput,
  Modal,
  Select,
  SelectOptionType,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { toDateOnlyPayload } from '../../../../../../utils/dates';
import { CurrencyOverviewItem, createCurrencyRate } from '../api';

const dictPrefix = 'financialPortal.settings.currencies';

interface FormValues {
  currencyId: SelectOptionType | null;
  value: string;
  validFrom: Date | null;
}

const EMPTY_VALUES: Pick<FormValues, 'value' | 'validFrom'> = {
  value: '',
  validFrom: null,
};

interface Props {
  isOpen: boolean;
  currencies: CurrencyOverviewItem[];
  onClose: () => void;
  onSaved: () => void;
}

export function NewRateModal({ isOpen, currencies, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { control, handleSubmit, formState, reset } = useForm<FormValues>({
    defaultValues: { currencyId: null, ...EMPTY_VALUES },
  });

  const currencyOptions = useMemo<SelectOptionType[]>(
    () =>
      currencies.map((currency) => ({
        value: currency.id,
        label: currency.code,
      })),
    [currencies],
  );

  // The modal never unmounts (it just toggles via `isOpen`), so the
  // `useForm` state otherwise survives a close/reopen: type a value, hit
  // Cancel, reopen — the stale value would still be there. Resetting on
  // every `isOpen`/`currencies` change covers both "closed" and "reopened".
  // The currency select is preselected only when there is exactly one
  // secondary currency to choose from. `reset` is stable across renders
  // (react-hook-form guarantees this), so it's safe to depend on.
  useEffect(() => {
    const preselected = currencies.length === 1 ? currencyOptions[0] : null;
    reset({ currencyId: preselected, ...EMPTY_VALUES });
  }, [isOpen, currencies, currencyOptions, reset]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!values.currencyId || !values.validFrom) {
        return;
      }
      // `toDateOnlyPayload` ya resuelve el desfase de día que produce
      // serializar un Date del form con toISOString(). No rehacerlo a mano.
      const validFrom = toDateOnlyPayload(values.validFrom);

      const { error } = await createCurrencyRate(
        Number(values.currencyId.value),
        {
          value: Number(values.value),
          validFrom,
        },
      );
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.createError`),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.createSuccess`),
      });
      // No explicit `reset()` here: closing the modal after a successful
      // save (see `onSaved`) sets `isOpen` back to `false` in the parent,
      // which the effect above already reacts to.
      onSaved();
    },
    [onSaved, t],
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <h1 className="text-dark fs-22 mb-4">{t(`${dictPrefix}.newRate`)}</h1>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Select
          name="currencyId"
          label={t(`${dictPrefix}.form.currency`)}
          control={control}
          options={currencyOptions}
          disabled={currencyOptions.length === 0}
          rules={{ required: t(`${dictPrefix}.form.required`) as string }}
          isClearable={false}
        />
        <TextInput
          name="value"
          label={t(`${dictPrefix}.form.value`)}
          control={control}
          rules={{
            required: t(`${dictPrefix}.form.required`) as string,
            // `raw` is typed `any` (not `string`) to match the codebase
            // convention for react-hook-form `validate` callbacks (see
            // `validateTextNotEmpty` in hooks/useValidations.ts): with a
            // multi-field FormValues, react-hook-form widens the value type
            // to the union of every field's type, so a narrower annotation
            // fails to typecheck here.
            validate: (raw: any) =>
              Number(raw) > 0 || (t(`${dictPrefix}.form.positive`) as string),
          }}
        />
        <DateInput
          name="validFrom"
          label={t(`${dictPrefix}.form.validFrom`)}
          control={control}
          rules={{ required: t(`${dictPrefix}.form.required`) as string }}
        />
        <Row className="mt-4">
          <Col xs={12} md={6}>
            <Button
              outlined
              fullwidth
              text={t(`${dictPrefix}.form.cancel`)}
              onClick={onClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              type="submit"
              fullwidth
              loading={formState.isSubmitting}
              text={t(`${dictPrefix}.form.save`)}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
